import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Plus, Search, Pencil, Trash2, X, Save, Mail, Phone, MapPin, Building2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminClients() {
  const { authHeaders } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const fetchClients = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/clients`, { headers: authHeaders })
      .then(r => setClients(r.data)).finally(() => setLoading(false));
  }, [authHeaders]);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  const filtered = clients.filter(c => {
    if (!search) return true;
    const s = search.toLowerCase();
    return c.nom?.toLowerCase().includes(s) ||
           c.email?.toLowerCase().includes(s) ||
           c.telephone?.includes(s) ||
           c.societe?.toLowerCase().includes(s);
  });

  const deleteClient = async (id) => {
    await axios.delete(`${API}/clients/${id}`, { headers: authHeaders });
    setDeleting(null);
    fetchClients();
  };

  const openEdit = (client) => {
    setEditClient(client);
    setShowModal(true);
  };

  const openCreate = () => {
    setEditClient(null);
    setShowModal(true);
  };

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="clients-title">Clients</h1>
          <p className="text-gray-500 text-sm mt-1">Gérez votre base de clients</p>
        </div>
        <button onClick={openCreate} data-testid="create-client-btn"
          className="flex items-center gap-2 px-5 py-2.5 bg-[#1B2E4B] text-white text-sm font-semibold hover:bg-[#162440] transition-colors rounded">
          <Plus size={16} /> AJOUTER UN CLIENT
        </button>
      </div>

      {/* Search */}
      <div className="bg-white p-4 shadow-sm mb-4">
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher par nom, email, téléphone, société..."
            data-testid="clients-search"
            className="w-full pl-9 pr-4 py-2.5 border border-gray-200 text-sm focus:outline-none focus:border-[#D4A843]" />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="clients-table">
            <thead>
              <tr className="border-b border-gray-100 text-xs text-gray-400 font-semibold tracking-wider uppercase bg-gray-50">
                {["Nom","Email","Téléphone","Ville","Société","Actions"].map(h => (
                  <th key={h} className="px-4 py-4 text-left whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="text-center py-12 text-gray-400">Chargement...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-12 text-gray-400">Aucun client trouvé</td></tr>
              ) : filtered.map(c => (
                <tr key={c.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <p className="font-semibold text-gray-900">{c.nom}</p>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5 text-gray-600">
                      <Mail size={12} className="text-gray-400" />
                      {c.email}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5 text-gray-600">
                      <Phone size={12} className="text-gray-400" />
                      {c.telephone}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{c.ville || "—"}</td>
                  <td className="px-4 py-3">
                    {c.societe ? (
                      <div className="flex items-center gap-1.5 text-gray-600">
                        <Building2 size={12} className="text-gray-400" />
                        {c.societe}
                      </div>
                    ) : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button onClick={() => openEdit(c)} data-testid={`edit-client-${c.id}`}
                        title="Modifier" className="text-gray-400 hover:text-gray-700 transition-colors">
                        <Pencil size={14} />
                      </button>
                      <button onClick={() => setDeleting(c.id)} data-testid={`delete-client-${c.id}`}
                        title="Supprimer" className="text-gray-400 hover:text-red-500 transition-colors">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Create/Edit */}
      {showModal && (
        <ClientModal
          client={editClient}
          authHeaders={authHeaders}
          onClose={() => setShowModal(false)}
          onSaved={() => { setShowModal(false); fetchClients(); }}
        />
      )}

      {/* Delete confirmation */}
      {deleting && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white p-6 max-w-sm w-full shadow-2xl">
            <h3 className="font-bold text-gray-900 mb-3">Supprimer ce client ?</h3>
            <p className="text-gray-500 text-sm mb-6">Cette action est irréversible.</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeleting(null)} className="px-5 py-2 border border-gray-200 text-sm font-semibold">Annuler</button>
              <button onClick={() => deleteClient(deleting)} data-testid="confirm-delete-client"
                className="px-5 py-2 bg-red-500 text-white text-sm font-semibold hover:bg-red-600">Supprimer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ClientModal({ client, authHeaders, onClose, onSaved }) {
  const isEdit = !!client;
  const [form, setForm] = useState({
    nom: client?.nom || "",
    email: client?.email || "",
    telephone: client?.telephone || "",
    adresse: client?.adresse || "",
    ville: client?.ville || "",
    societe: client?.societe || "",
    siret: client?.siret || "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      if (isEdit) {
        await axios.put(`${API}/clients/${client.id}`, form, { headers: authHeaders });
      } else {
        await axios.post(`${API}/clients`, form, { headers: authHeaders });
      }
      onSaved();
    } catch (err) { setError(err.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h2 className="font-bold text-gray-900">{isEdit ? "Modifier le client" : "Nouveau client"}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          {error && <p className="text-red-500 text-sm bg-red-50 p-3">{error}</p>}
          
          {[
            ["nom", "Nom complet *", "text", true],
            ["email", "Email *", "email", true],
            ["telephone", "Téléphone *", "tel", true],
            ["adresse", "Adresse", "text", false],
            ["ville", "Ville", "text", false],
            ["societe", "Société", "text", false],
            ["siret", "SIRET", "text", false],
          ].map(([key, label, type, required]) => (
            <div key={key}>
              <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">{label}</label>
              <input type={type} value={form[key]} onChange={e => set(key, e.target.value)}
                required={required} data-testid={`client-${key}`}
                className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4A843]" />
            </div>
          ))}

          <div className="flex gap-3 pt-2 justify-end border-t border-gray-100">
            <button type="button" onClick={onClose} className="px-5 py-2 border border-gray-200 text-sm font-semibold hover:bg-gray-50">Annuler</button>
            <button type="submit" disabled={loading} data-testid="save-client-btn"
              className="flex items-center gap-2 px-6 py-2 bg-[#1B2E4B] text-white text-sm font-semibold hover:bg-[#162440] disabled:opacity-60 rounded">
              <Save size={14} /> {loading ? "Sauvegarde..." : (isEdit ? "Modifier" : "Créer")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
